import API from "../../../../urls/api";
import PATH from "../../../../urls/path";
import { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import useOnBlur from "../../../../scripts/hooks/useOnBlur";
import { ResourceType } from "../../../../interfaces/general";
import { post, responseValidator } from "../../../../scripts/api";
import Spinner1 from "../../../../utils/component/spinners/spinner1/spinner1";
import { ReactComponent as Close } from "../../../../assets/images/sidebar/Times.svg";
import { ReactComponent as SearchIcon } from "../../../../assets/images/icons/Search.svg";
import { isTouchScreen, searchUrlToObject, searchUrlToString } from "../../../../scripts/helpers";
import { handleSetDefaultSelectedItem } from "../../../../utils/component/fileManagers/fileManager.actions";
import SearchResultBox from "./SearchResultBox";
import { SearchPageType } from "./search.interface";
import HistorySearch from "./HistorySearch";

const maxLength = 5;
const maxLengthSearchResult = 250;

type SearchState = { myFile: { results: ResourceType[]; count: number }; withShareMe: { results: ResourceType[]; count: number } };

const initialSearchState: SearchState = { myFile: { results: [], count: 0 }, withShareMe: { results: [], count: 0 } };

const Search = (): JSX.Element => {
    const navigate = useNavigate();
    const searchResultRef = useRef<any>();
    const [open, setOpen] = useState(false);
    const [loading, setLoading] = useState(false);
    const searchParamsValue = searchUrlToObject()?.name || "";
    const [searchValue, setSearchValue] = useState<string>(searchParamsValue);
    const [searchResults, setSearchResults] = useState<SearchState>(initialSearchState);
    const { pathname } = useLocation();
    useOnBlur(searchResultRef, () => setOpen(false));

    useEffect(() => {
        setSearchValue(searchParamsValue);
    }, [searchParamsValue]);

    useEffect(() => {
        let id: NodeJS.Timeout | null = null;
        if (!searchValue) {
            setSearchResults(initialSearchState);
            setLoading(false);
        } else if (searchValue && searchValue.length > maxLengthSearchResult) {
            setOpen(true);
            setLoading(false);
        } else {
            setLoading(true);
            id = setTimeout(async () => {
                let newSearchResults = Object.assign({}, searchResults);

                await new Promise((resolve) => {
                    post(
                        (data, status) => {
                            if (responseValidator(status)) newSearchResults.myFile = { results: data.results, count: data.count };
                            resolve(true);
                        },
                        API.reporting.search(),
                        { scroll_id: null, name: searchValue, next_page: false, page_size: maxLength }
                    );
                });

                await new Promise((resolve) => {
                    post(
                        (data, status) => {
                            if (responseValidator(status))
                                newSearchResults.withShareMe = {
                                    results: data.results.map((item: any) => ({ ...item, ...item.obj })),
                                    count: data.count
                                };
                            resolve(true);
                        },
                        API.reporting.sharedSearch() + `?limit=${maxLength}`,
                        { name: searchValue }
                    );
                });
                setSearchResults(newSearchResults);
                setLoading(false);
            }, 500);
        }

        return () => {
            if (id) clearTimeout(id);
        };
    }, [searchValue]);

    const handleClearSearch = (keepValue?: boolean) => {
        if (!keepValue) {
            if (searchParamsValue) {
                const params = window.location.search.replace(/name:[^|]+(\|?)/, "");
                navigate(pathname + params);
            }
            setSearchResults(initialSearchState);
            setSearchValue("");
        }
        setOpen(false);
    };

    const onItemClick = (resource: ResourceType, pageType: SearchPageType) => {
        const path = pageType === SearchPageType.MY_FILE ? PATH.files : PATH.shared;

        if (resource.is_trash) navigate(PATH.trash);
        else if (resource.parent_hash && pageType === SearchPageType.WITH_SHARE_ME) navigate(`${path}?path=${resource.parent_hash}`);
        else if (resource.parent && pageType === SearchPageType.MY_FILE) navigate(`${path}?path=${resource.parent}`);
        else navigate(path);

        setTimeout(() => {
            handleSetDefaultSelectedItem([resource.id.toString()]);
            setOpen(false);
        }, 500);
    };

    const onShowAllClick = (pageType: SearchPageType) => {
        handleClearSearch(true);

        let qs = `?q=name:${searchValue}|pageType:${pageType}`;
        if (window.location.pathname === PATH.search) {
            const searchUrlObject = searchUrlToObject();
            qs = searchUrlToString({ ...searchUrlObject, name: searchValue, pageType });
        }
        navigate(PATH.search + qs);
    };

    const onEnterHandler = () => {
        if (loading) return;
        handleClearSearch(true);

        let pageType = !searchResults.myFile.count && searchResults.withShareMe.count ? SearchPageType.WITH_SHARE_ME : SearchPageType.MY_FILE;

        if (searchUrlToObject()?.pageType) pageType = searchUrlToObject()?.pageType;
        let qs = `?q=name:${searchValue}|pageType:${pageType}`;
        if (window.location.pathname === PATH.search) {
            const searchUrlObject = searchUrlToObject();
            qs = searchUrlToString({ ...searchUrlObject, name: searchValue, pageType });
        }

        navigate(PATH.search + qs);
    };

    const hasTouchScreen = useMemo(() => !!isTouchScreen(), []);

    return (
        <>
            <div ref={searchResultRef} className="relative flex items-center gap-4 shrink-1 w-full flex-1">
                <div
                    id="nbv"
                    className={`transition-all border-[1.5px] ${!searchValue ? "bg-secondary-200 border-transparent" : "bg-white border-[#50A5FF]"} px-4 ${hasTouchScreen ? "h-[40px]" : "h-[54px]"} w-full rounded-[10px] flex gap-4 items-center justify-between`}
                >
                    {/* input search */}
                    <SearchIcon className="shrink-0 " onClick={() => !!searchValue && onEnterHandler()} />
                    <input
                        onClick={() => setOpen(true)}
                        type="text"
                        placeholder={searchParamsValue ? `عبارت جستجو شده "${searchParamsValue}"` : "جستجو نام فایل یا پوشه مورد نظر"}
                        value={searchValue}
                        onChange={(e) => setSearchValue(e.target.value)}
                        onKeyDown={(e) => e.key === "Enter" && !!searchValue && onEnterHandler()}
                        className="w-full bg-transparent placeholder:text-secondary-400 text-[14px] font-normal outline-none focus:outline-none"
                    />
                    <div className="flex justify-between">
                        {(searchValue || searchParamsValue) && !loading && (
                            <Close className="cursor-pointer w-fit" onClick={() => handleClearSearch()} />
                        )}
                        {loading && <Spinner1 />}
                    </div>
                    {/* myFile search results */}
                    {open && (
                        <div
                            onClick={() => setOpen(false)}
                            className={`fixed inset-0 z-[60] sm:absolute sm:inset-auto sm:right-0  sm:max-h-[70svh] overflow-auto no-scrollbar sm:top-[113%] sm:rounded-[10px] w-full py-2 sm:z-30 overflow-y-auto bg-[#f6f8ff] sm:bg-white sm:border-[1px] border-secondary-300 ${hasTouchScreen ? "top-[104px]" : "top-[75px]"}`}
                            style={{ boxShadow: "0px 16px 60px rgba(134, 151, 170, 0.1)" }}
                        >
                            {searchValue ? (
                                <>
                                    {/* my file */}
                                    <SearchResultBox
                                        title="فایل های من"
                                        maxLength={maxLength}
                                        onShowAllClick={() => onShowAllClick(SearchPageType.MY_FILE)}
                                        searchValue={searchValue}
                                        onItemClick={(resource) => onItemClick(resource, SearchPageType.MY_FILE)}
                                        count={searchResults.myFile.count}
                                        contentSearchResults={searchResults.myFile.results}
                                    />

                                    {!!searchResults.myFile.count && !!searchResults.withShareMe.count && (
                                        <hr className="w-full h-[2px] bg-primary-400 my-2" />
                                    )}

                                    {/* share file */}
                                    <SearchResultBox
                                        title="مشترک با من"
                                        maxLength={maxLength}
                                        onShowAllClick={() => onShowAllClick(SearchPageType.WITH_SHARE_ME)}
                                        searchValue={searchValue}
                                        onItemClick={(resource) => onItemClick(resource, SearchPageType.WITH_SHARE_ME)}
                                        count={searchResults.withShareMe.count}
                                        contentSearchResults={searchResults.withShareMe.results}
                                    />

                                    {loading && (
                                        <p className="py-2 px-4">
                                            <span className="font-medium text-[14px]">لطفا کمی صبر کنید ...</span>
                                        </p>
                                    )}

                                    {/* no result */}
                                    {!loading && !searchResults.myFile.count && !searchResults.withShareMe.count && (
                                        <p className="py-2 px-4 flex items-center gap-1">
                                            <span className="font-medium text-[14px]">نتیجه‌ای یافت نشد.</span>
                                            {searchValue.length > maxLengthSearchResult && (
                                                <span className="font-normal text-[13px] text-secondary-500">
                                                    (تعداد کاراکتر بیش از حد مجاز است.)
                                                </span>
                                            )}
                                        </p>
                                    )}
                                </>
                            ) : (
                                <HistorySearch setOpen={setOpen} handleClearSearch={handleClearSearch} />
                            )}
                        </div>
                    )}
                </div>
            </div>
        </>
    );
};

export default Search;
